// /* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import { IContactDto, IContactFilter } from '../Contact.model'
import { useContactSearchMutation } from '../Contact.api'
import classNames from 'classnames'
import { TagInputSearchListControls } from '../../../common/component/TagInputSearchListControls'
import { IFilterDto } from '../../../common/model/Core.model'
import { MessageType } from '../../../common/component/MessageToast'
import { ContactDashboardListItem } from './ContactDashboardListItem'

interface Props {
  label: string
  required: boolean
  enabled: boolean
  selectOnClick?: (contact: IContactDto) => void
}

export const ContactPicker: FC<Props> = ({label, required, enabled, selectOnClick}: Props) => {
  const intl = useIntl()
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  const contactFilterKeys = {
    defaultKey: IContactFilter.DETAIL,
    defaultKeyList: [
      IContactFilter.ID,
      IContactFilter.DETAIL,
    ],
  }
  const didFetchContact = useRef(false)
  const [contactFilterSuggestions, setContactFilterSuggestions] = useState<string[]>(
    contactFilterKeys.defaultKeyList
  )
  const [contactFilters, setContactFilters] = useState<IFilterDto[]>([])
  const [contactSearchLoading, setContactSearchLoading] = useState(false)
  const [contactSearchResult, setContactSearchResult] = useState<IContactDto[] | undefined>(undefined)
  const [contact, setContact] = useState<IContactDto | undefined>(undefined) 
  const [contactSearch] = useContactSearchMutation()

  const contactSearchOnClickHandler = async () => {
    try {
      if (!contact && !didFetchContact.current) {
        setContactSearchLoading(true)
        const response: any = await contactSearch({filters: contactFilters})
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          setContactSearchResult(response.data.data)
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setContactSearchLoading(false)
    }
  }

  const contactResetSearchResetOnClickHandler = async () => {
    setContactFilterSuggestions(contactFilterKeys.defaultKeyList)
    setContactSearchResult(undefined)
  }

  const contactRowOnClickHandler = async (contactId) => {
    if (contactSearchResult) {
      const contact = contactSearchResult.find((contact) => contact.id === contactId)
      if (contact) {
        setContact(contact)
        setContactSearchResult(undefined)
        selectOnClick && selectOnClick(contact)
      }
    }
  }

  return (
    <>
      <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
        <span
          className={classNames({
            'text-gray-700': !required,
            required: required,
          })}
        >
          {label}
        </span>
      </label>

      <div className='col-xxl-9'>
        <TagInputSearchListControls
          loading={contactSearchLoading}
          enabled={enabled}
          filterKeys={contactFilterKeys}
          filterSuggestions={contactFilterSuggestions}
          filters={contactFilters}
          createOnClickPath={'/contacts/new'}
          setFilterSuggestions={setContactFilterSuggestions}
          setFilters={setContactFilters}
          searchOnClickHandler={contactSearchOnClickHandler}
          resetSearchResetOnClickHandler={contactResetSearchResetOnClickHandler}
        ></TagInputSearchListControls>
      </div>

      <label className='col-xxl-3'></label>
      <div className='col-xxl-9 h-3px'>
        {contactSearchResult && contactSearchResult?.length > 0 && (
          <ul id='contact-autocomplete-list' className='suggestions-list' role='listbox'>
            {contactSearchResult.map((contact, index) => (
              <li key={index} onClick={() => contactRowOnClickHandler(contact.id)}>
                <ContactDashboardListItem data={contact} rowSelected={contact.id} />
                <div className='separator'></div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  )
}
